import { useCallback, useEffect, useRef, useState } from "react";
import { Box, Button, LinearProgress, Tooltip } from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../store";
import FileUpload from "../../components/FileUpload";
import { getCustomerUploadStatus } from "../../store/selectors/CustomerUploadSelectors";
import { resetCustomerUpload } from "../../store/reducers/CustomerUploadReducer";
import { uploadCustomerImageAction } from "../../store/actions/CustomerUploadActions";
import InstructionOverlay from "../../components/InstructionOverlay/InstructionOverlay";
import InfoIcon from "@mui/icons-material/Info";
import { onRunPredictionAction } from "../../store/actions/PredictionActions";
import { getPredictionData, getPredictionRunningState } from "../../store/selectors/PredictionSelectors";
import { updatePredictionRunningState } from "../../store/reducers/PredictionReducers";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import "./Predict.scss";

const Predict = () => {
  const dispatch = useAppDispatch();
  const uploadStatus = useAppSelector(getCustomerUploadStatus);
  const predictionRunningState = useAppSelector(getPredictionRunningState);
  const predictionData = useAppSelector(getPredictionData);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [showInstructionOverlay, setShowInstructionOverlay] = useState(false);
  const skipFirstUploadStatusEffect = useRef(true);

  // Reset shared upload/prediction state on mount so we don't show step 2 or loading from a previous flow (e.g. Predict Screws).
  useEffect(() => {
    setCurrentStep(1);
    setSelectedFile(null);
    dispatch(resetCustomerUpload());
    dispatch(updatePredictionRunningState("idle"));
    return function cleanup() {
      setSelectedFile(null);
      dispatch(resetCustomerUpload());
      dispatch(updatePredictionRunningState("idle"));
    };
  }, [dispatch]);

  const handleFileSubmission = useCallback(() => {
    if (!selectedFile) return;
    const formData = new FormData();
    formData.append("image", selectedFile, selectedFile.name);
    dispatch(uploadCustomerImageAction(formData));
  }, [selectedFile, dispatch]);

  const fileUploadProps = useCallback(
    () => ({
      isFileUploading: uploadStatus === "loading",
      selectedFile,
      showCancel: false,
      onFileChange: setSelectedFile,
      onCancelUpload: () => console.log("Not implemented"),
      handleFileSubmission,
      modelType: "locator",
      allowMultiple: false, // Predictions should only allow single files
    }),
    [selectedFile, uploadStatus, handleFileSubmission]
  );

  useEffect(() => {
    // Skip first run so we never react to stale "success" from the other predict page when switching.
    if (skipFirstUploadStatusEffect.current) {
      skipFirstUploadStatusEffect.current = false;
      return;
    }
    if (uploadStatus === "success") {
      setCurrentStep(2);
      dispatch(onRunPredictionAction());
    }
  }, [uploadStatus, dispatch]);

  const renderPredictions = useCallback(() => {
    if (predictionRunningState === "loading") {
      return (
        <div className="prediction-loading">
          <Box sx={{ width: "100%", maxWidth: 400, margin: "0 auto" }}>
            <p>Our AI model is analyzing your image...</p>
            <LinearProgress />
          </Box>
        </div>
      );
    }

    if (predictionRunningState === "success" && predictionData) {
      return (
        <div className="prediction-results">
          <div className="prediction-disclaimer">
          <p>Our AI model has analyzed your image and provided potential matches. Please keep in mind that these are preliminary suggestions and <b>may not be entirely accurate</b>.</p>
          <p>Our team will review your submission and send a <b>verified response via email</b> within one business day.</p>
          </div>
          <div className="predictions-grid">
            {predictionData.slice(0, 3).map((prediction, index) => (
              <div key={index} className="prediction-card">
                <div className="prediction-header">
                  <h3>Prediction {index + 1}</h3>
                </div>
                <div className="confidence">
                  Confidence: {prediction.confidence.toFixed(1)}%
                  <Tooltip 
                    title="Confidence indicates how certain our AI model is about this match. A higher percentage means the model is more confident about the prediction." 
                    arrow 
                    placement="top"
                    className="confidence-tooltip"
                  >
                    <InfoOutlinedIcon className="info-icon" fontSize="small" />
                  </Tooltip>
                </div>
                <div className="prediction-details">
                  <p><strong>Type:</strong> {prediction.typename}</p>
                  <p><strong>Length:</strong> {prediction.length}mm</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }

    return null;
  }, [predictionRunningState, predictionData]);

  const pageContent = useCallback(() => {
    switch (currentStep) {
      case 1:
        return (
          <>
            <div className="instruction-button-wrapper">
              <Button
                variant="text"
                className="instruction-button animate"
                onClick={() => setShowInstructionOverlay(true)}
              >
                <InfoIcon />
                Instructions
              </Button>
            </div>
            {showInstructionOverlay && (
              <InstructionOverlay onClose={() => setShowInstructionOverlay(false)}>
                <p>Drag and drop an image of the locator, or click to select an image from your device.</p>

                <p>For best results:</p>
                <ul>
                  <li>Use a plain, neutral background</li>
                  <li>Ensure the locator is centered and clearly visible</li>
                  <li>Make sure no other objects are in the photo besides the locator</li>
                  <li>Avoid extreme camera angles and direct sunlight</li>
                  <li>Verify that all edges of the locator are visible</li>
                </ul>

                <p>Examples:</p>
                <div className="instruction-image-wrapper">
                  <img src="/images/locator-example-1.jpg" alt="Locator example 1" />
                  <img src="/images/locator-example-2.jpg" alt="Locator example 2" />
                  <img src="/images/locator-example-3.jpg" alt="Locator example 3" />
                </div>
              </InstructionOverlay>
            )}
            <div className="predict-layout">
              <div className="predict-left-side">
                <div className="instruction-box good-picture">
                  <span>Good picture</span>
                </div>
                <div className="instruction-box only-one-locator">
                  <span>Only one locator<br/>on the picture</span>
                </div>
                <div className="instruction-box white-background">
                  <span>Plain background</span>
                </div>
                <div className="instruction-box complete-locator">
                  <span>Complete locator<br/>visible</span>
                </div>
              </div>
              <div className="predict-center">
                <FileUpload {...fileUploadProps()} />
              </div>
              <div className="predict-right-side">
                <div className="example-section">
                  <span className="example-label">Good example</span>
                  <div className="example-image">
                    <img src="/images/locator-example-4.jpg" alt="Locator example" />
                  </div>
                </div>
              </div>
            </div>
          </>
        );
      case 2:
        return (
          <div className="upload-success-view">
            <div className="success-content">
              {(predictionRunningState === "loading" || !predictionData) && (
                <div className="success-message">
                  <img src="/images/SuccessIcon.jpg" alt="Success" className="success-icon" />
                  <h2>Thank you for your submission</h2>
                  <p>We will review your image and send you a verified response within one business day.</p>
                  <p>If you want, you can wait to see the results from our AI model. This may take a minute.</p>
                </div>
              )}
              {renderPredictions()}
              <Button
                variant="contained"
                className="btn btn-blue"
                onClick={() => {
                  setCurrentStep(1);
                  setSelectedFile(null);
                  dispatch(resetCustomerUpload());
                  dispatch(updatePredictionRunningState("idle"));
                }}
              >
                Upload Another Image
              </Button>
            </div>
          </div>
        );
      default:
        return <Box>Something went wrong</Box>;
    }
  }, [currentStep, fileUploadProps, showInstructionOverlay, dispatch, predictionRunningState, predictionData, renderPredictions]);

  return pageContent();
};

export default Predict;
